import { t } from "@lingui/macro";
import {
  CalendarBlankIcon,
  DownloadSimpleIcon,
  GiftIcon,
  SparkleIcon,
  StarIcon,
  XIcon,
} from "@phosphor-icons/react";
import type { NotificationPopup, UserDto } from "@reactive-resume/dto";
import { getNotificationPopup } from "@reactive-resume/dto";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { markNotificationAsRead } from "@/client/services/notification/api";
import { NOTIFICATION_KEYS, useNotificationPopups } from "@/client/services/notification/queries";
import { useTopbarHighlight } from "@/client/stores/topbar-highlight";

const CHIP_ICONS = {
  download: DownloadSimpleIcon,
  calendar: CalendarBlankIcon,
  gift: GiftIcon,
  star: StarIcon,
  sparkle: SparkleIcon,
};

// Le compteur reste mis en évidence quelques secondes après la fermeture.
const HIGHLIGHT_AFTER_CLOSE_MS = 6000;

type NotificationPopupCardProps = {
  title: string;
  body?: string | null;
  popup: NotificationPopup;
  onAction: () => void;
  onDismiss: () => void;
};

export const NotificationPopupCard = ({
  title,
  body,
  popup,
  onAction,
  onDismiss,
}: NotificationPopupCardProps) => {
  const reduceMotion = useReducedMotion();

  return (
    <motion.section
      className={cn(
        "relative w-full overflow-hidden rounded-[24px]",
        "border border-indigo-100 bg-white px-5 py-5 sm:px-7 sm:py-6",
        "shadow-[0_24px_70px_rgba(39,48,112,0.16)]",
      )}
      initial={reduceMotion ? false : { opacity: 0, y: 22, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 -top-24 size-52 rounded-full bg-indigo-200/30 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-24 -left-24 size-48 rounded-full bg-emerald-200/25 blur-3xl"
      />

      <button
        type="button"
        aria-label={t`Fermer`}
        onClick={onDismiss}
        className={cn(
          "absolute right-4 top-4 z-10 grid size-8 place-items-center rounded-full",
          "text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-200",
        )}
      >
        <XIcon size={18} weight="bold" />
      </button>

      <div className="relative flex items-start gap-3.5 pr-10">
        <img
          src="/logo/mon-cv-pro_logo.svg"
          alt="Mon CV Pro"
          className="h-11 w-auto shrink-0 sm:h-12"
        />

        <div className="min-w-0 flex-1">
          {popup.eyebrow && (
            <span className="block text-[10px] font-black uppercase tracking-[0.16em] text-emerald-600">
              {popup.eyebrow}
            </span>
          )}

          <DialogTitle className="mt-1 text-[1.2rem] font-extrabold leading-tight tracking-[-0.025em] text-[#071451] sm:text-[1.35rem]">
            {title}
          </DialogTitle>
        </div>
      </div>

      <DialogDescription className="relative mt-3 text-[0.8rem] font-medium leading-6 text-slate-500">
        {body}
      </DialogDescription>

      {popup.chips && popup.chips.length > 0 && (
        <ul className="relative mt-4 flex flex-wrap gap-2">
          {popup.chips.map((chip) => {
            const Icon = chip.icon ? CHIP_ICONS[chip.icon] : null;

            return (
              <li
                key={chip.label}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[0.72rem]",
                  chip.icon === "calendar"
                    ? "bg-emerald-50 text-emerald-800"
                    : "bg-indigo-50 text-indigo-800",
                  chip.strong ? "font-extrabold" : "font-semibold",
                )}
              >
                {Icon && <Icon aria-hidden size={15} weight="duotone" />}
                {chip.label}
              </li>
            );
          })}
        </ul>
      )}

      {popup.cta && (
        <motion.button
          type="button"
          onClick={onAction}
          className={cn(
            "relative mt-5 inline-flex min-h-11 w-full items-center justify-center gap-2",
            "overflow-hidden rounded-xl bg-gradient-to-r from-[#667eea] via-[#5f6ddd] to-[#5867d2]",
            "px-5 text-[0.78rem] font-extrabold text-white",
            "shadow-[0_10px_24px_rgba(79,70,229,0.26)] transition-all duration-200",
            "hover:shadow-[0_14px_30px_rgba(79,70,229,0.34)]",
            "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-indigo-200",
          )}
          whileHover={reduceMotion ? undefined : { y: -2, scale: 1.005 }}
          whileTap={reduceMotion ? undefined : { scale: 0.985 }}
        >
          {popup.cta.label}
        </motion.button>
      )}

      <button
        type="button"
        onClick={onDismiss}
        className="relative mt-3 w-full rounded-lg py-1.5 text-center text-[0.74rem] font-semibold text-slate-500 transition-colors hover:text-slate-700"
      >
        {popup.dismissLabel ?? t`Plus tard`}
      </button>
    </motion.section>
  );
};

type Props = {
  user: UserDto;
};

// Affiche, une à une, les notifications non lues qui portent `data.popup` ; les fermer les marque comme lues.
export const NotificationPopupHost = ({ user }: Props) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setHighlight = useTopbarHighlight((state) => state.setMetric);
  const [handled, setHandled] = useState<string[]>([]);

  const ready = Boolean(user.countryPreferenceSetAt) && !pathname.startsWith("/client/auth");
  const { data } = useNotificationPopups(ready);

  const current = (data ?? [])
    .filter((notification) => !handled.includes(notification.id))
    .map((notification) => ({ notification, popup: getNotificationPopup(notification.data) }))
    .find((item) => item.popup !== null);

  const highlight = current?.popup?.highlight ?? null;

  useEffect(() => {
    if (highlight) {
      setHighlight(highlight);
      return;
    }

    const timer = window.setTimeout(() => setHighlight(null), HIGHLIGHT_AFTER_CLOSE_MS);
    return () => window.clearTimeout(timer);
  }, [highlight, setHighlight]);

  if (!ready || !current?.popup) return null;

  const { notification, popup } = current;

  const close = () => {
    setHandled((ids) => [...ids, notification.id]);
    void markNotificationAsRead(notification.id)
      .catch(() => null)
      .finally(() => queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.all }));
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent
        className={cn(
          "w-[calc(100vw-1rem)] max-w-[460px] rounded-[26px] border-0 bg-transparent p-2 shadow-none",
          "sm:w-[calc(100vw-2rem)]",
          "[&>button:last-child]:hidden",
        )}
      >
        <NotificationPopupCard
          title={notification.title ?? ""}
          body={notification.body}
          popup={popup}
          onDismiss={close}
          onAction={() => {
            close();
            if (popup.cta) void navigate(popup.cta.link);
          }}
        />
      </DialogContent>
    </Dialog>
  );
};
